/**
 * Notification Service — Budget Management (011)
 */

import { supabase, getCurrentUserId } from '../lib/supabase';
import type { BudgetAlert } from '../types/budget.types';
import type { BillReminderPayload } from '../types/bill.types';
import {
  UserNotification,
  NotificationPreferences,
  NotificationServiceResponse,
  NotificationType,
} from '../types/notification.types';

const DEFAULT_PREFS: NotificationPreferences = {
  budgetAlerts: true,
  billReminders: true,
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

class NotificationService {
  async getPreferences(userId?: string): Promise<NotificationPreferences> {
    void userId;
    return DEFAULT_PREFS;
  }

  async listUnread(userId?: string): Promise<NotificationServiceResponse<UserNotification[]>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const prefs = await this.getPreferences(uid);
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', uid)
        .is('dismissed_at', null)
        .order('created_at', { ascending: false });

      if (error) return { error: { code: 'DB_ERROR', message: error.message } };

      let rows = data ?? [];
      if (!prefs.budgetAlerts) rows = rows.filter((r) => r.type !== 'budget_alert');
      if (!prefs.billReminders) rows = rows.filter((r) => r.type !== 'bill_reminder');

      return {
        data: rows.map((r) => ({
          id: r.id,
          type: r.type as NotificationType,
          title: r.title,
          message: r.message,
          severity: r.severity as UserNotification['severity'],
          sourceType: r.source_type as UserNotification['sourceType'],
          sourceId: r.source_id,
          createdAt: r.created_at,
          readAt: r.read_at ?? undefined,
        })),
      };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async getUnreadCount(userId?: string): Promise<NotificationServiceResponse<number>> {
    const result = await this.listUnread(userId);
    if (result.error) return { error: result.error };
    return { data: (result.data ?? []).filter((n) => !n.readAt).length };
  }

  async markRead(id: string, userId?: string): Promise<NotificationServiceResponse<{ success: true }>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { error } = await supabase
        .from('user_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', uid);

      if (error) return { error: { code: 'DB_ERROR', message: error.message } };
      return { data: { success: true } };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async dismiss(id: string, userId?: string): Promise<NotificationServiceResponse<{ success: true }>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { error } = await supabase
        .from('user_notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', uid);

      if (error) return { error: { code: 'DB_ERROR', message: error.message } };
      return { data: { success: true } };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async syncBudgetAlerts(alerts: BudgetAlert[], userId?: string): Promise<void> {
    const uid = userId ?? (await getCurrentUserId());
    if (!uid) return;

    const prefs = await this.getPreferences(uid);
    if (!prefs.budgetAlerts) return;

    const material = alerts.filter(
      (a) => a.severity === 'warning' || a.severity === 'critical'
    );

    for (const alert of material) {
      const sourceId = alert.id.replace(/^(alert|success)-/, '');
      if (!isUuid(sourceId)) {
        console.warn('syncBudgetAlerts: skip non-UUID source_id', sourceId);
        continue;
      }

      const { error } = await supabase.from('user_notifications').upsert(
        {
          user_id: uid,
          type: 'budget_alert',
          title: alert.title,
          message: alert.message,
          severity: alert.severity,
          source_type: 'budget_category',
          source_id: sourceId,
          dismissed_at: null,
        },
        { onConflict: 'user_id,type,source_type,source_id', ignoreDuplicates: false }
      );
      if (error) {
        console.error('syncBudgetAlerts: upsert failed', error.message, error);
      }
    }
  }

  async syncBillReminders(userId: string, reminders: BillReminderPayload[]): Promise<void> {
    const prefs = await this.getPreferences(userId);
    if (!prefs.billReminders) return;

    const activeIds = reminders.map((r) => r.id).filter(isUuid);

    for (const bill of reminders) {
      if (!isUuid(bill.id)) continue;

      const title = bill.autopay
        ? bill.severity === 'info'
          ? `${bill.name} autopay`
          : `${bill.name} autopay review`
        : `${bill.name} due`;
      const { error } = await supabase.from('user_notifications').upsert(
        {
          user_id: userId,
          type: 'bill_reminder',
          title,
          message: bill.message,
          severity: bill.severity,
          source_type: 'bill',
          source_id: bill.id,
          dismissed_at: null,
        },
        { onConflict: 'user_id,type,source_type,source_id', ignoreDuplicates: false }
      );
      if (error) {
        console.error('syncBillReminders: upsert failed', error.message, error);
      }
    }

    let dismissQuery = supabase
      .from('user_notifications')
      .update({ dismissed_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('type', 'bill_reminder')
      .is('dismissed_at', null);

    if (activeIds.length > 0) {
      dismissQuery = dismissQuery.not('source_id', 'in', `(${activeIds.join(',')})`);
    }

    const { error: dismissError } = await dismissQuery;
    if (dismissError) {
      console.error('syncBillReminders: dismiss stale failed', dismissError.message, dismissError);
    }
  }
}

export const notificationService = new NotificationService();

export async function listUnread(userId?: string) {
  return notificationService.listUnread(userId);
}
export async function getUnreadCount(userId?: string) {
  return notificationService.getUnreadCount(userId);
}
export async function markRead(id: string, userId?: string) {
  return notificationService.markRead(id, userId);
}
export async function dismiss(id: string, userId?: string) {
  return notificationService.dismiss(id, userId);
}
export async function syncBudgetAlerts(alerts: BudgetAlert[], userId?: string) {
  return notificationService.syncBudgetAlerts(alerts, userId);
}
export async function syncBillReminders(userId: string, reminders: BillReminderPayload[]) {
  return notificationService.syncBillReminders(userId, reminders);
}
